import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { FeatureCollection, Geometry, GeoJsonProperties } from 'geojson';
import { SKIP_LOADER } from '../auth/loader.interceptor';

export interface GeoParseResponse {
  success?: boolean;
  message?: string;
  error?: string;
  id?: string;
  filename?: string;
  features?: number;
  missing_files?: string[];
  // NEW: server returns parsed GeoJSON so we can render/edit it
  geojson?: FeatureCollection<Geometry, GeoJsonProperties>;
}

@Injectable({ providedIn: 'root' })
export class GeoUploadService {
  private readonly http = inject(HttpClient);
  private readonly base = 'https://geo-service.supplycanvas.com/api'; // If no proxy, use 'http://localhost:8083/api'

  health(endpoint = `${this.base}/geo/health`): Observable<string> {
    return this.http.get(endpoint, {
      observe: 'body',
      responseType: 'text',
    });
  }

  // Kept for compatibility, but not used by saveAndClose anymore
  private geojsonToFile(geojson: unknown, filename?: string): File {
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    const name = filename ?? `polygons-${ts}.geojson`;
    const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/geo+json' });
    return new File([blob], name, { type: 'application/geo+json' });
  }

  uploadFile(file: File, endpoint = `${this.base}/geo/parse`): Observable<HttpEvent<GeoParseResponse>> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<GeoParseResponse>(endpoint, form, {
      reportProgress: true,
      observe: 'events',
      context: new HttpContext().set(SKIP_LOADER, true),
    });
  }

  // Helper: upload any supported file and yield the final parsed response (not progress events)
  parseFileToGeoJSON(
    file: File,
    onProgress?: (pct: number) => void,
    endpoint = `${this.base}/geo/parse`
  ): Observable<GeoParseResponse> {
    return this.uploadFile(file, endpoint).pipe(
      map((event) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          const pct = Math.round((event.loaded * 100) / event.total);
          onProgress?.(pct);
          return undefined;
        }
        if (event instanceof HttpResponse) {
          return event.body as GeoParseResponse;
        }
        return undefined;
      }),
      (src) =>
        new Observable<GeoParseResponse>((obs) =>
          src.subscribe({
            next: (v) => {
              if (v !== undefined) obs.next(v);
            },
            error: (e) => obs.error(e),
            complete: () => obs.complete(),
          })
        )
    );
  }

  // Still available if you ever want to upload raw GeoJSON (not used by saveAndClose anymore)
  uploadGeoJSON(
    geojson: unknown,
    onProgress?: (pct: number) => void,
    endpoint = `${this.base}/geo/parse`
  ): Observable<GeoParseResponse> {
    const file = this.geojsonToFile(geojson);
    return this.parseFileToGeoJSON(file, onProgress, endpoint);
  }
}
